import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "redux/hooks";
import { RootState } from "redux/store";
import { coverPageAssignedNumberDelete, coverPageAssignedUserDelete, coverPageAssignNumberUpdate, coverPageAssignUserUpdate, coverPageCheck, coverPageCreate, coverPageDelete, coverPagesGet, numberListGet, userListGet } from "redux/services/coverPageService";

interface AssignedUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
}

interface AssignedNumber {
  id: string;
  did: string;
  description: string;
}

export interface CoverPageType {
  id: string;
  name: string;
  description: string;
  created_on: string;
  user_id: number;
  assigned_users: AssignedUser[];
  assigned_numbers: AssignedNumber[];
}

interface InitialState {
  coverPages: {
    coverpages: CoverPageType[];
    total: number;
    page: number;
    limit: number;
    status?: string;
  };
}

const initialState: InitialState = {
  coverPages: {
    coverpages: [],
    total: 0,
    page: 1,
    limit: 10,
  },
};

export const getNumberList = createAsyncThunk(
  "coverPage/get/number/list",
  async () => {
    return await numberListGet();
  }
);

export const getUserList = createAsyncThunk(
  "coverPage/get/user/list",
  async () => {
    return await userListGet();
  }
);

export const getCoverPages = createAsyncThunk(
  "coverPage/get",
  async (params: any) => {
    return await coverPagesGet(params);
  }
);

export const createCoverPage = createAsyncThunk(
  "coverPage/create",
  async (data: any) => {
    return await coverPageCreate(data);
  }
);

export const deleteCoverPage = createAsyncThunk(
  "coverPage/delete",
  async (id: number) => {
    return await coverPageDelete(id);
  }
);

export const updateCoverPageAssignUser = createAsyncThunk(
  "coverPage/update/assign/user",
  async ({ coverpage_id, user_id }: { coverpage_id: number; user_id: number }) => {
    return await coverPageAssignUserUpdate(coverpage_id, user_id);
  }
);

export const updateCoverPageAssignNumber = createAsyncThunk(
  "coverPage/update/assign/number",
  async ({ coverpage_id, number_id }: { coverpage_id: number; number_id: number }) => {
    return await coverPageAssignNumberUpdate(coverpage_id, number_id);
  }
);

export const deleteCoverPageAssignUser = createAsyncThunk(
  "coverPage/delete/assign/user",
  async ({ coverpage_id, user_id }: { coverpage_id: number; user_id: number }) => {
    return await coverPageAssignedUserDelete(coverpage_id, user_id);
  }
);

export const deleteCoverPageAssignNumber = createAsyncThunk(
  "coverPage/delete/assign/number",
  async ({ coverpage_id, number_id }: { coverpage_id: number; number_id: number }) => {
    return await coverPageAssignedNumberDelete(coverpage_id, number_id);
  }
);

export const checkCoverPage = createAsyncThunk(
  "coverPage/check",
  async ({ user_id, number_id }: { user_id: number; number_id: number }) => {
    return await coverPageCheck(user_id, number_id);
  }
);

const coverPageSlice = createSlice({
  name: "coverPage",
  initialState,
  reducers: {
    clearSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      getCoverPages.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.coverPages = action.payload;
      }
    );
  },
});

export default coverPageSlice.reducer;
export const { clearSlice } = coverPageSlice.actions;

export const selectCoverPages = (state: RootState) =>
  state.coverPage.coverPages;
export const useCoverPages = () => {
  const coverPages = useAppSelector(selectCoverPages);
  return coverPages;
};
